import { useCallback, useEffect, useRef, useState } from 'react';

// 16rem, matching the literal the stylesheet falls back to when nothing has set
// the custom property.
const DEFAULT_WIDTH = 256;
// Narrow enough to be a hint of a list, wide enough that a long question still
// wraps to two lines rather than being trimmed to nothing.
const MIN_WIDTH = 180;
const MAX_WIDTH = 520;
const KEYBOARD_STEP = 16;
const STORAGE_KEY = 'aegateway.chat.sidebarWidth';

const clamp = (value) => Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, Math.round(value)));

// Read before the first paint rather than in an effect, so a remembered split
// does not show up as the sidebar jumping once on load.
const readStoredWidth = () => {
    try {
        const stored = Number(window.localStorage.getItem(STORAGE_KEY));
        return stored ? clamp(stored) : DEFAULT_WIDTH;
    } catch (error) {
        // Private mode, or storage full. The default width is fine.
        return DEFAULT_WIDTH;
    }
};

/**
 * Drag-to-resize state for the history sidebar, returned as the width in pixels
 * plus the props for the handle that changes it. The width is remembered per
 * browser, so the split survives a reload the way the history itself does.
 */
export const useResizableSidebar = () => {
    const [width, setWidth] = useState(readStoredWidth);
    const [dragging, setDragging] = useState(false);
    // Where the pointer went down and how wide the sidebar was then: deltas from
    // the start beat summing per-move deltas, which drift once the value clamps.
    const origin = useRef(null);

    // Written on release and on each keypress rather than on every pointermove,
    // which would hit localStorage a few hundred times per drag.
    const remember = useCallback((value) => {
        try {
            window.localStorage.setItem(STORAGE_KEY, String(value));
        } catch (error) {
            // Nothing to recover: the width still applies for this session.
        }
    }, []);

    const applyAndRemember = useCallback(
        (value) => {
            const next = clamp(value);
            setWidth(next);
            remember(next);
        },
        [remember]
    );

    // A drag that starts on the handle ends up selecting the transcript instead
    // once the pointer crosses into it, so selection is off for the duration.
    useEffect(() => {
        if (!dragging) {
            return undefined;
        }

        const { body } = document;
        const previous = body.style.userSelect;
        body.style.userSelect = 'none';
        return () => {
            body.style.userSelect = previous;
        };
    }, [dragging]);

    const onPointerDown = useCallback(
        (event) => {
            // Left button only: a right-click drag is a context menu, not a resize.
            if (event.pointerType === 'mouse' && event.button !== 0) {
                return;
            }

            origin.current = { x: event.clientX, width };
            // Capture keeps the moves coming to the handle even when the pointer
            // runs ahead of it, so no window-level listeners are needed.
            event.currentTarget.setPointerCapture(event.pointerId);
            setDragging(true);
        },
        [width]
    );

    const onPointerMove = useCallback((event) => {
        if (!origin.current) {
            return;
        }
        setWidth(clamp(origin.current.width + event.clientX - origin.current.x));
    }, []);

    const onPointerUp = useCallback(() => {
        if (!origin.current) {
            return;
        }
        origin.current = null;
        setDragging(false);
        remember(width);
    }, [remember, width]);

    const onKeyDown = useCallback(
        (event) => {
            if (event.key === 'ArrowLeft') {
                event.preventDefault();
                applyAndRemember(width - KEYBOARD_STEP);
            } else if (event.key === 'ArrowRight') {
                event.preventDefault();
                applyAndRemember(width + KEYBOARD_STEP);
            } else if (event.key === 'Home') {
                event.preventDefault();
                applyAndRemember(DEFAULT_WIDTH);
            }
        },
        [applyAndRemember, width]
    );

    return {
        width,
        dragging,
        // Spread onto the handle: a separator the pointer drags, the keyboard
        // nudges, and a double-click puts back where it started.
        handleProps: {
            role: 'separator',
            'aria-orientation': 'vertical',
            'aria-label': 'Resize conversation history',
            'aria-valuenow': width,
            'aria-valuemin': MIN_WIDTH,
            'aria-valuemax': MAX_WIDTH,
            tabIndex: 0,
            title: 'Drag to resize · double-click to reset',
            onPointerDown,
            onPointerMove,
            onPointerUp,
            onPointerCancel: onPointerUp,
            onKeyDown,
            onDoubleClick: () => applyAndRemember(DEFAULT_WIDTH)
        }
    };
};

export default useResizableSidebar;
